import Styles from './Images.module.scss'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { SelectedImage } from 'types'
import { collectResults } from 'utils/query'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Button } from 'blaze-ui/Button'
import { Grid } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import {
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useModals } from 'almanac-ui/Modal'
import { DragOverlayForFirstUpload } from 'components/Design/UploadsTab/DragOverlay'
import { compact } from 'lodash'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useDropzone } from 'react-dropzone'
import { useRef } from 'react'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { MediaFilePreview } from './MediaFilePreview'
import { AllUploads } from '../Providers/Uploads/AllUploads'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

interface Props {
  onSelectImage: (value: SelectedImage) => void
  lastSelectedImage?: SelectedImage
  onSelectImageFromAll?: (value: SelectedImage) => void
  isDisabled?: boolean
}

export const LibraryImages = ({ onSelectImage, lastSelectedImage, onSelectImageFromAll, isDisabled }: Props) => {
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const imagePickerContext = useImagePickerContext()
  const { openModal, closeModal } = useModals()
  const { selectedImages, accept } = imagePickerContext
  const uploadsQuery = useUploads()
  const { uploadImageFiles } = useImportMediaFiles()
  const workspaceId = useCurrentWorkspaceId()
  const { data } = useUploadsProject(workspaceId)
  const uploads = collectResults(uploadsQuery, (data) => data.files)
  const mediaFiles = getMediaFiles(uploads, accept)
  const mediaFilesAsSelectedFiles: SelectedImage[] = mediaFiles
    .map((mf) => ({ ...mf, url: mf.source_url as string }))
    .slice(0, 4)
  const inputRef = useRef<HTMLInputElement>(null)

  const onDrop = (files: File[]) => {
    if (data?.project?.id) {
      uploadImageFiles(files, data?.project.id)
      return
    }

    showToastMessage('Unable to upload file.', 'error')
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept || ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  })

  const withVideo = accept?.includes(ACCEPTED_VIDEO_MIME_TYPES)

  const handleOpenAll = () => {
    openModal(
      AllUploads,
      {
        ...imagePickerContext,
        accept: accept || ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
        onConfirm: (images) => {
          onSelectImage(images[0])
          closeModal()
          onSelectImageFromAll?.(images[0])
        },
      },
      { newStack: true },
    )
  }

  const images = compact([lastSelectedImage].concat(mediaFilesAsSelectedFiles))
    .filter((img) => !!img.source_url)
    .slice(0, 4)

  if (uploadsQuery.isLoading || uploadsQuery.isFetching)
    return (
      <div className={Styles.imagesSection}>
        <div className={Styles.header}>
          <Text variant="largeList">or from your image library</Text>
        </div>
        <div className={Styles.imagesCard} {...getRootProps()}>
          <div className={Styles.imagesGrid}>
            {Array.from({ length: 4 }).map((_, i) => (
              <ShimmeringColor
                aspectRatio={1}
                width={isDesktopWizardsUpdatesOn ? 152 : 141}
                height={isDesktopWizardsUpdatesOn ? 152 : 141}
                key={i}
              />
            ))}
          </div>
          <Button frontIcon={Grid} variant="tertiary" isDisabled>
            Select another from my {withVideo ? 'Media' : 'Image'} Library
          </Button>
        </div>
      </div>
    )

  return (
    <div className={Styles.imagesSection}>
      <div className={Styles.header}>
        <Text variant="largeList">or from your image library</Text>
      </div>
      <div className={Styles.imagesCard} {...getRootProps()}>
        {images.length > 0 ? (
          <>
            <div className={Styles.imagesGrid}>
              {images.map((file) => (
                <MediaFilePreview
                  key={file.id}
                  mediaFile={file}
                  isDisabled={isDisabled}
                  isSelected={file.id === selectedImages[0]?.id}
                  alt="uploaded to library"
                  showVideoPreview={false}
                />
              ))}
              {Array.from({ length: 4 - images.length }).map((_, i) => (
                <div key={i} className={Styles.emptyPlaceholder} />
              ))}
            </div>
            <Button frontIcon={Grid} variant="tertiary" onPress={handleOpenAll} isDisabled={isDisabled}>
              Select another from my Image Library
            </Button>
          </>
        ) : (
          <>
            <DragOverlayForFirstUpload
              style={{
                minHeight: isDesktopWizardsUpdatesOn ? 314 : 290,
                width: isDesktopWizardsUpdatesOn ? 314 : 290,
                margin: 0,
                border: '3px dashed var(--dark-15, rgba(0, 0, 0, 0.15))',
                boxShadow: 'none',
              }}
              active={isDragActive}
              withSupportedFilesTag={false}
            />
            <input {...getInputProps()} ref={inputRef} />
          </>
        )}
      </div>
    </div>
  )
}
